import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createChart } from "lightweight-charts";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { QueryBoundary, UpdatedAt, retryTransient } from "@/lib/queryState";
import { useLiveWindow } from "@/lib/marketHours";
import { PageHead, Panel, DataStateBadge, SegmentedControl, StatCell, fmt, lwcOptions, usePrefs, sessionDateLabel, etAndLocal } from "@/sentinel";

/** The prior close the engine used for this session: the latest snapshot's prior_close source entry. */
function enginePriorClose(sources) {
  const snaps = sources?.snapshots || [];
  for (let i = snaps.length - 1; i >= 0; i -= 1) {
    const s = snaps[i];
    const e = (s.source_log?.entries || []).find((x) => (x.input || x.field) === "prior_close" && typeof x.value === "number");
    if (e) return e.value;
    if (typeof s.payload?.prior_close === "number") return s.payload.prior_close;
  }
  return null;
}

const tok = (name) => getComputedStyle(document.documentElement).getPropertyValue("--" + name).trim();

export default function Charts() {
  const { selectedDate } = useAuth();
  const { theme } = usePrefs();
  const [kind, setKind] = useState("line"); // candles are opt-in, never the default
  const win = useLiveWindow(selectedDate);
  const query = useQuery({ queryKey: ["series", selectedDate], queryFn: () => api.getSeries(selectedDate), enabled: !!selectedDate, retry: retryTransient, refetchInterval: win.live ? 60000 : false });
  const { data: sources } = useQuery({ queryKey: ["sources", selectedDate], queryFn: () => api.getSources(selectedDate), enabled: !!selectedDate, retry: retryTransient });
  const engineClose = enginePriorClose(sources);
  // The series endpoint's prior close can be the vendor's latest "previous day" rather than this session's.
  // The engine's recorded value for the selected session wins.
  const data = useMemo(() => (query.data ? { ...query.data, vendor_prior_close: query.data.prior_close, prior_close: engineClose ?? query.data.prior_close } : query.data), [query.data, engineClose]);
  const ref = useRef(null);
  const paintRef = useRef(null);

  const bars = data?.series || [];
  const vwapOf = (b) => b.vwap ?? b.vw ?? null;
  const hasVwap = bars.some((b) => vwapOf(b) != null);

  useEffect(() => {
    if (!data || !ref.current || !bars.length) return;
    const el = ref.current;
    el.innerHTML = "";
    // Fixture bar times are ET wall-clock encoded as UTC; format back in UTC to show the ET label.
    const fmtHM = (t) =>
      new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" }).format(t * 1000);
    const o0 = lwcOptions();
    const chart = createChart(el, {
      width: el.clientWidth,
      height: 440,
      localization: { locale: "en-US", timeFormatter: fmtHM, priceFormatter: (p) => fmt(p, "index") },
      layout: o0.layout,
      grid: o0.grid,
      crosshair: o0.crosshair,
      rightPriceScale: o0.rightPriceScale,
      timeScale: { ...o0.timeScale, tickMarkFormatter: (t) => fmtHM(t) },
    });
    const toTs = (t) => Math.floor(Date.parse(`${selectedDate}T${t}:00Z`) / 1000);

    const price = kind === "candles"
      ? chart.addCandlestickSeries({})
      : chart.addLineSeries({ ...o0.series.price, lineWidth: 2 });
    price.setData(kind === "candles"
      ? bars.map((b) => ({ time: toTs(b.time), open: b.open, high: b.high, low: b.low, close: b.close }))
      : bars.map((b) => ({ time: toTs(b.time), value: b.close })));

    // VWAP: dashed, no last-value tag. Drawn only when the bars carry it.
    const vwap = hasVwap ? chart.addLineSeries(o0.series.vwap) : null;
    if (vwap) vwap.setData(bars.filter((b) => vwapOf(b) != null).map((b) => ({ time: toTs(b.time), value: vwapOf(b) })));

    const openLine = data.official_open != null ? price.createPriceLine({ price: data.official_open, ...o0.series.officialOpen, title: "Official open" }) : null;
    const priorLine = data.prior_close != null ? price.createPriceLine({ price: data.prior_close, color: tok("text-tertiary"), lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "Prior close" }) : null;

    // Re-reads the live tokens and applies them — on creation and whenever the theme changes.
    const paint = () => {
      const o = lwcOptions();
      chart.applyOptions({ layout: o.layout, grid: o.grid, crosshair: o.crosshair, rightPriceScale: o.rightPriceScale, timeScale: { borderColor: o.timeScale.borderColor } });
      if (kind === "candles") {
        price.applyOptions({ upColor: o.series.up, downColor: o.series.down, borderUpColor: o.series.up, borderDownColor: o.series.down, wickUpColor: o.series.up, wickDownColor: o.series.down });
      } else {
        price.applyOptions({ color: o.series.price.color });
      }
      if (vwap) vwap.applyOptions({ color: o.series.vwap.color });
      if (openLine) openLine.applyOptions({ color: o.series.officialOpen.color });
      if (priorLine) priorLine.applyOptions({ color: tok("text-tertiary") });
      const flag = tok("text-secondary");
      price.setMarkers([
        { time: toTs("09:30"), position: "belowBar", color: flag, shape: "arrowUp", text: "Open" },
        { time: toTs("09:45"), position: "aboveBar", color: flag, shape: "square", text: "OR15" },
        { time: toTs("10:00"), position: "aboveBar", color: flag, shape: "square", text: "OR30" },
      ].filter((m) => bars.some((b) => toTs(b.time) === m.time)));
    };
    paint();
    paintRef.current = paint;
    chart.timeScale().fitContent();

    const onResize = () => chart.applyOptions({ width: el.clientWidth });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      paintRef.current = null;
      chart.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedDate, kind]);

  // Theme change: the provider sets data-theme in its own effect, so read the tokens on the next frame.
  useEffect(() => {
    const id = requestAnimationFrame(() => paintRef.current && paintRef.current());
    return () => cancelAnimationFrame(id);
  }, [theme]);

  const isLive = data?.provider === "polygon";
  const head = (
    <PageHead
      crumb={["Evidence", <span className="sn-sans-num">{sessionDateLabel(selectedDate)}</span>]}
      title="Charts"
      context={<>{isLive ? (win.live ? "Live" : win.isToday ? "Saved" : "Archived") : "Stub"} · SPX minute bars for <span className="sn-sans-num">{sessionDateLabel(selectedDate)}</span>{isLive ? " · massive.com Indices Advanced (real-time)" : " · synthetic, not market data"}</>}
      actions={
        <SegmentedControl label="Chart type" value={kind} onChange={setKind}
          options={[{ value: "line", label: "Line", testId: "chart-type-line" }, { value: "candles", label: "Candles", testId: "chart-type-candles" }]} />
      }
    />
  );

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="charts-page">
      {head}
      <QueryBoundary query={query} loading={6} what={`the intraday series for ${selectedDate}`} errorTestId="charts-error"
        isEmpty={(d) => !d?.series?.length}
        empty={{ icon: "ChartCandlestick", title: "No intraday series", body: data?.note || "No intraday series for this session.", testId: "charts-empty" }}>
        {() => {
          const last = bars[bars.length - 1];
          const lastClose = last?.close;
          const chg = lastClose != null && data.prior_close != null ? lastClose - data.prior_close : null;
          const pct = chg != null && data.prior_close ? (chg / data.prior_close) * 100 : null;
          const state = !isLive ? "STUB" : win.live ? "LIVE" : "FROZEN";
          const numState = !isLive ? "stub" : win.live ? "live" : "frozen";
          const ariaLabel = `SPX ${kind === "candles" ? "candles" : "line"}, ${selectedDate}, ${bars[0].time} to ${etAndLocal(last.time, { day: selectedDate })}. Last ${fmt(lastClose, "index")}, official open ${fmt(data.official_open, "index")}, prior close ${fmt(data.prior_close, "index")}.${isLive ? "" : " Synthetic stub data."}`;
          const vendorDiffers = data.vendor_prior_close != null && engineClose != null && Math.abs(data.vendor_prior_close - engineClose) > 0.005;
          return (
            <>
              <Panel title={`SPX · 1 minute · ${sessionDateLabel(selectedDate)}`} icon="ChartCandlestick"
                meta={<><DataStateBadge state={state} /><span className="sn-num">{last?.time ? etAndLocal(last.time, { day: selectedDate }) : ""}</span>{win.live && <UpdatedAt at={query.dataUpdatedAt} />}</>}>
                <div className="sn-stack">
                  <div className="sn-row" style={{ gap: "var(--space-6)" }}>
                    <StatCell size="md" label="Last" value={lastClose} kind="index" state={numState} delta={chg ?? undefined}
                      sub={pct != null ? <span className="sn-num sn-faint">({pct > 0 ? "+" : pct < 0 ? "−" : ""}{Math.abs(pct).toFixed(2)}%)</span> : null} />
                    <StatCell size="md" label="Official open" value={data.official_open} kind="index" />
                    <StatCell size="md" label="Prior close" value={data.prior_close} kind="index" data-testid="charts-prior-close"
                      sub={engineClose != null ? "as used by the engine" : null} />
                    <StatCell size="md" label="VIX" value={data.vix} kind="index" />
                    <StatCell size="md" label="VIX1D" value={data.vix1d} kind="index" />
                    {data.or30 && <StatCell size="md" label="OR30 range" value={`${fmt(data.or30.low, "index")} – ${fmt(data.or30.high, "index")}`}
                      sub={data.or15 ? <span className="sn-num">OR15 {fmt(data.or15.low, "index")} – {fmt(data.or15.high, "index")}</span> : null} />}
                  </div>
                  <figure role="img" aria-label={ariaLabel} style={{ margin: 0, position: "relative" }}>
                    <div ref={ref} data-testid="spx-chart" style={{ width: "100%" }} />
                    {!isLive && (
                      <span aria-hidden="true" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none",
                        fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: 56, color: "var(--provenance)", opacity: "var(--opacity-watermark)" }}>STUB</span>
                    )}
                  </figure>
                  <div className="sn-row sn-small sn-muted" style={{ gap: "var(--space-4)" }}>
                    <span className="sn-row" style={{ gap: 6 }}><svg width="18" height="4" aria-hidden="true"><line x1="0" y1="2" x2="18" y2="2" stroke="var(--viz-price)" strokeWidth="2" /></svg>SPX</span>
                    {hasVwap && <span className="sn-row" style={{ gap: 6 }}><svg width="18" height="4" aria-hidden="true"><line x1="0" y1="2" x2="18" y2="2" stroke="var(--viz-2)" strokeWidth="1.5" strokeDasharray="6 4" /></svg>VWAP</span>}
                    <span className="sn-row" style={{ gap: 6 }}><svg width="18" height="4" aria-hidden="true"><line x1="0" y1="2" x2="18" y2="2" stroke="var(--text-secondary)" strokeWidth="1" strokeDasharray="1 3" /></svg>Official open</span>
                    <span className="sn-row" style={{ gap: 6 }}><svg width="18" height="4" aria-hidden="true"><line x1="0" y1="2" x2="18" y2="2" stroke="var(--text-tertiary)" strokeWidth="1" strokeDasharray="4 3" /></svg>Prior close</span>
                  </div>
                </div>
              </Panel>
              <p className="sn-small sn-faint" style={{ margin: 0 }}>
                Markers: official open (09:30), OR15 (09:45), OR30 (10:00).{!hasVwap && " No VWAP: these bars carry no volume."}
                {vendorDiffers && <> Vendor prior close <span className="sn-num">{fmt(data.vendor_prior_close, "index")}</span> differs; the engine&apos;s value for this session is shown.</>}
              </p>
            </>
          );
        }}
      </QueryBoundary>
    </div>
  );
}
